import React, { useEffect, useState } from "react";
import { portalApi, formatPortalError, getPortalUser } from "@/api/portalClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, User as UserIcon, KeyRound } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("fi-FI", { style: "currency", currency: "EUR" }).format(Number(n || 0));

export default function EmployeePortalProfile() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pwOld, setPwOld] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await portalApi.get("/employee-portal/me");
        setMe(r.data);
      } catch (e) { toast.error(formatPortalError(e)); }
      setLoading(false);
    })();
  }, []);

  const changePassword = async (e) => {
    e?.preventDefault();
    if (pwNew.length < 8) return toast.error("Uusi salasana on vähintään 8 merkkiä");
    setPwSaving(true);
    try {
      await portalApi.post("/employee-portal/change-password", { current_password: pwOld, new_password: pwNew });
      toast.success("Salasana vaihdettu");
      setPwOld(""); setPwNew("");
    } catch (err) { toast.error(formatPortalError(err)); }
    setPwSaving(false);
  };

  if (loading) return <div className="text-center py-12 text-white/40"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4 max-w-3xl" data-testid="emp-portal-profile">
      <div>
        <h1 className="text-2xl font-display gradient-text-luxe">Profiili</h1>
        <p className="text-xs text-white/40 mt-1">Omat tietosi (vain luku). Pyydä työnantajaltasi muutoksia.</p>
      </div>

      <Section title="Henkilötiedot" icon={<UserIcon className="w-4 h-4" />}>
        <Grid>
          <Item label="Nimi" value={`${me?.first_name || ""} ${me?.last_name || ""}`.trim() || "—"} />
          <Item label="Sähköposti (portaali)" value={me?.email || "—"} />
          <Item label="Puhelin" value={me?.phone || "—"} />
          <Item label="Tilinumero (IBAN)" value={me?.iban || "—"} />
          <Item label="Osoite" value={me?.address || "—"} />
          <Item label="Postinumero / Kaupunki" value={`${me?.postal_code || ""} ${me?.city || ""}`.trim() || "—"} />
        </Grid>
      </Section>

      <Section title="Työsuhteen tiedot">
        <Grid>
          <Item label="Tyyppi" value={
            { monthly: "Kuukausipalkkainen", hourly: "Tuntipalkkainen", gig: "Keikkatyöntekijä", yel: "Yrittäjä" }[me?.employment_type] || me?.employment_type || "—"
          } />
          <Item label="Aloituspäivä" value={me?.start_date || "—"} />
          {me?.employment_type === "monthly"
            ? <Item label="Kuukausipalkka" value={eur(me?.monthly_salary)} />
            : <Item label="Tuntipalkka" value={eur(me?.hourly_rate) + " / h"} />}
          <Item label="Verokortti perus-%" value={`${me?.tax_card?.base_percent || 0} %`} />
          <Item label="Verokortti lisä-%" value={`${me?.tax_card?.additional_percent || 0} %`} />
          <Item label="Tuloraja" value={eur(me?.tax_card?.income_threshold || 0)} />
        </Grid>
      </Section>

      <Section title="Vaihda salasana" icon={<KeyRound className="w-4 h-4" />}>
        <form onSubmit={changePassword} className="space-y-3 max-w-md">
          <div>
            <Label className="text-xs text-white/60">Nykyinen salasana</Label>
            <Input type="password" value={pwOld} onChange={e => setPwOld(e.target.value)} className="mt-1 bg-black/30 border-white/10" data-testid="pw-old" />
          </div>
          <div>
            <Label className="text-xs text-white/60">Uusi salasana (vähintään 8 merkkiä)</Label>
            <Input type="password" value={pwNew} onChange={e => setPwNew(e.target.value)} className="mt-1 bg-black/30 border-white/10" data-testid="pw-new" />
          </div>
          <Button type="submit" disabled={pwSaving} className="btn-luxe inline-flex items-center gap-2" data-testid="pw-save">
            {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Tallenna uusi salasana
          </Button>
        </form>
      </Section>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <div className="flex items-center gap-2 mb-3">
        {icon && <span className="text-amber-300">{icon}</span>}
        <h3 className="text-sm font-display text-white">{title}</h3>
      </div>
      {children}
    </div>
  );
}
function Grid({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>; }
function Item({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-white/40">{label}</p>
      <p className="text-sm text-white mt-1">{value}</p>
    </div>
  );
}
